import { useMutation } from 'react-query'
import { useSelector } from 'react-redux'
import { useAuth0 } from '../../contexts/auth0-context'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import { uploadFile } from '../../API/PostRequests'

type BatchDraftAI = {
    title?: string
    abstract?: string
    year?: string | number
    authors?: string | string[]
    citation?: string
    shortCitation?: string
    longCitation?: string
    url?: string
    PMID?: string
    category?: string
}

type UploadFileAndCreateDraftVariables = {
    file: File
    aiData: BatchDraftAI
}

type UseUploadFileAndCreateDraftMutationProps = {
    onSuccess?: (draftId: string) => void
    onError?: (error: unknown) => void
}

export const useUploadFileAndCreateDraftMutation = ({
    onSuccess,
    onError,
}: UseUploadFileAndCreateDraftMutationProps = {}) => {
    const { getTokenSilently } = useAuth0()

    // @ts-expect-error store is not typed properly
    const strategyId = useSelector((state) => state.strategy?._id)
    // @ts-expect-error store is not typed properly
    const organizationId = useSelector((state) => state.strategy?.organizationId)

    const uploadAndCreate = async ({ file, aiData }: UploadFileAndCreateDraftVariables): Promise<string> => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        const timestamp = Date.now().toString()
        await uploadFile({ link: file }, timestamp, 'evidence', token)

        const newDraft = await callAPI(endpoints.createDraft.url, endpoints.createDraft.method, token, {
            name: (aiData.title || file.name).replace(/\n+/gm, ' '),
            s3Name: timestamp,
            link: aiData.url || '',
            mimeType: file.type,
            originalFileName: file.name,
            abstract: aiData.abstract || '',
            category: aiData.category || '---',
            year: aiData.year ? aiData.year.toString() : '2025',
            internal: false,
            author: Array.isArray(aiData.authors) ? aiData.authors.join(', ') : aiData.authors || '',
            citation: aiData.longCitation || aiData.shortCitation || aiData.citation || '',
            shortCitation: aiData.shortCitation || '',
            longCitation: aiData.longCitation || '',
            pubmedId: aiData.PMID || '',
            strategyId,
            organizationId,
            status: 'draft',
        })

        return newDraft?.id || newDraft?._id || null
    }

    return useMutation(uploadAndCreate, {
        onSuccess: (draftId) => onSuccess?.(draftId),
        onError: (error) => {
            console.error('[useUploadFileAndCreateDraftMutation] Failed:', error)
            onError?.(error)
        },
    })
}
